/* eslint-disable no-undef */
import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import {
  registerMemberLookupStart,
  clearMapSketchRegistration
} from 'redux/bydaMemberLookup/actions';
import { RegisterMemberLookupMapSketchBody } from 'services/bydaMemberLookup.service';
import { MemberLookupProcessingPhase } from 'redux/bydaMemberLookup/types';
import { MemberLookupWizardInputMode } from './useBydaMemberLookupWizard';
import { DrawingResult } from './useMemberLookupMap';

const REGISTER_DEBOUNCE_MS = 500;

interface UseMapSketchRegistrationOptions {
  inputMode: MemberLookupWizardInputMode;
  drawnShapes: DrawingResult[];
  processingPhase: MemberLookupProcessingPhase;
  lookupId: number | null;
  pendingContinue: boolean;
}

function useMapSketchRegistration({
  inputMode,
  drawnShapes,
  processingPhase,
  lookupId,
  pendingContinue
}: UseMapSketchRegistrationOptions): void {
  const dispatch = useDispatch();
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const prevShapeCountRef = useRef(0);
  const hadSuccessfulRegisterRef = useRef(false);
  const processingPhaseRef = useRef(processingPhase);
  const lookupIdRef = useRef(lookupId);
  const pendingContinueRef = useRef(pendingContinue);

  processingPhaseRef.current = processingPhase;
  lookupIdRef.current = lookupId;
  pendingContinueRef.current = pendingContinue;

  useEffect(() => {
    if (processingPhase === 'AWAITING_PAYMENT' && lookupId !== null) {
      hadSuccessfulRegisterRef.current = true;
    }
    if (drawnShapes.length === 0) {
      hadSuccessfulRegisterRef.current = false;
    }
  }, [processingPhase, lookupId, drawnShapes.length]);

  useEffect(() => {
    function clearRegistrationOnModeChange() {
      if (inputMode === 'draw') return;

      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }

      if (
        lookupId !== null &&
        (processingPhase === 'AWAITING_PAYMENT' ||
          processingPhase === 'REGISTERING')
      ) {
        dispatch(clearMapSketchRegistration());
      }
    }

    clearRegistrationOnModeChange();
  }, [inputMode, lookupId, processingPhase, dispatch]);

  useEffect(() => {
    function clearDebounceTimer() {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
    }

    function scheduleMapSketchRegistration() {
      if (inputMode !== 'draw') return;

      const shapeCount = drawnShapes.length;
      const prevCount = prevShapeCountRef.current;
      prevShapeCountRef.current = shapeCount;

      const currentPhase = processingPhaseRef.current;
      const currentLookupId = lookupIdRef.current;
      const isPendingContinue = pendingContinueRef.current;

      if (shapeCount === 0) {
        clearDebounceTimer();
        if (
          currentLookupId !== null ||
          currentPhase === 'AWAITING_PAYMENT' ||
          currentPhase === 'REGISTERING'
        ) {
          dispatch(clearMapSketchRegistration());
        }
        return;
      }

      if (isPendingContinue && currentLookupId !== null) {
        clearDebounceTimer();
        return;
      }

      const payload: RegisterMemberLookupMapSketchBody = {
        inputSource: 'MAP_SKETCH',
        shapes: drawnShapes.map((s) => s.polygon)
      };

      const isFirstShape = prevCount === 0 && shapeCount === 1;
      const isInFlightChange = currentPhase === 'REGISTERING';
      const isPostSuccessChange =
        hadSuccessfulRegisterRef.current && shapeCount > 0;

      clearDebounceTimer();

      if (isFirstShape || isInFlightChange) {
        dispatch(registerMemberLookupStart(payload));
      } else if (isPostSuccessChange) {
        debounceTimerRef.current = setTimeout(() => {
          dispatch(registerMemberLookupStart(payload));
        }, REGISTER_DEBOUNCE_MS);
      }
    }

    scheduleMapSketchRegistration();

    return clearDebounceTimer;
  }, [drawnShapes, inputMode, dispatch]);
}

export default useMapSketchRegistration;
